import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { showsInstructorProfile } from "@lms/backend/lib/tenants/public-profile";
import { Badge, Heading, PageShell, Text } from "@/components/ui";
import { buttonClasses } from "@/components/ui/button";
import { fetchTenantCatalog } from "@/services/api/tenants";
import {
  BIOGRAPHY_KEYS,
  INSTRUCTOR_IMAGES,
  SPECIALISM_KEYS,
} from "@/pages/tenant/instructor-profile-copy";

/**
 * The About page at `/t/:tenantSlug/about` — the instructor profile and the full
 * biography.
 *
 * Same tenancy rule as the landing page: `showsInstructorProfile` decides, and
 * every other tenant gets a neutral page naming itself and its catalogue instead
 * of a page about somebody they have never heard of.
 */
export function TenantAboutPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const t = useTranslations("tenantPublic");

  const catalogQuery = useQuery({
    queryKey: ["tenant-catalog", tenantSlug],
    queryFn: () => fetchTenantCatalog(tenantSlug),
  });

  if (catalogQuery.isLoading) {
    return (
      <PageShell data-testid="tenant-about-loading">
        <Text tone="muted">{t("loading")}</Text>
      </PageShell>
    );
  }

  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <PageShell data-testid="tenant-about-error">
        <Heading as="h1" level="page">
          {t("tenantUnavailable")}
        </Heading>
      </PageShell>
    );
  }

  const { tenant, courses } = catalogQuery.data;
  const coursesHref = `/t/${tenantSlug}/courses`;

  const browseCoursesLink = (
    <Link
      to={coursesHref}
      className={buttonClasses({ variant: "accent", size: "lg", pill: true })}
      data-testid="about-browse-courses"
    >
      {t("browseCourses")}
    </Link>
  );

  if (!showsInstructorProfile(tenantSlug)) {
    return (
      <PageShell
        width="prose"
        pad="none"
        className="flex min-h-[min(60vh,40rem)] flex-1 flex-col justify-center py-12 sm:py-16"
        data-testid="tenant-about"
      >
        <Heading as="h1" level="page">
          {t("tenantAboutTitle", { tenant: tenant.name })}
        </Heading>
        <Text tone="muted" size="lg" className="mt-4">
          {t("tenantAboutBody", { tenant: tenant.name })}
        </Text>
        <Text tone="muted" size="sm" className="mt-2">
          {t("courseCount", { count: courses.length })}
        </Text>
        <div className="mt-8">{browseCoursesLink}</div>
      </PageShell>
    );
  }

  return (
    <PageShell
      width="app"
      pad="none"
      className="flex flex-1 flex-col gap-14 py-10 sm:py-14"
      data-testid="tenant-about"
    >
      {/* Instructor profile — photograph, specialisms. */}
      <header className="grid gap-8 md:grid-cols-[minmax(0,17rem)_1fr] md:gap-10">
        <img
          src={INSTRUCTOR_IMAGES.profile.src}
          width={INSTRUCTOR_IMAGES.profile.width}
          height={INSTRUCTOR_IMAGES.profile.height}
          alt={t("profileAlt")}
          loading="lazy"
          className="mx-auto w-full max-w-xs rounded-2xl border border-[var(--accent-border)] object-cover shadow-md md:mx-0"
        />
        <div>
          <Heading as="p" level="eyebrow">
            {tenant.name}
          </Heading>
          <Heading as="h1" level="page" className="mt-3">
            {t("aboutTitle")}
          </Heading>

          <div className="mt-6" data-testid="tenant-about-specialisms">
            <Heading as="h2" level="card" className="text-sm">
              {t("specialismsTitle")}
            </Heading>
            <ul className="mt-3 flex flex-wrap gap-2">
              {SPECIALISM_KEYS.map((key) => (
                <li key={key}>
                  <Badge tone="accent">{t(key)}</Badge>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </header>

      {/* The full biography, paragraph for paragraph. */}
      <section aria-labelledby="about-biography-title">
        <Heading as="h2" id="about-biography-title" level="section">
          {t("biographyTitle")}
        </Heading>
        <div className="mt-5 max-w-3xl space-y-5" data-testid="tenant-about-biography">
          {BIOGRAPHY_KEYS.map((key) => (
            <Text key={key} tone="secondary" size="base">
              {t(key)}
            </Text>
          ))}
        </div>
      </section>

      {/* Philosophy — kept out of the biography block so it reads as the close. */}
      <section aria-labelledby="about-philosophy-title" data-testid="tenant-about-philosophy">
        <Heading as="h2" id="about-philosophy-title" level="section">
          {t("philosophyTitle")}
        </Heading>
        <Text tone="muted" size="sm" className="mt-3">
          {t("philosophyIntro")}
        </Text>
        <blockquote className="mt-4 max-w-3xl border-l-2 border-l-[var(--accent-strong)] pl-5">
          <Text
            as="p"
            tone="primary"
            size="lg"
            className="font-serif text-xl leading-relaxed text-[var(--text-heading)]"
          >
            {t("philosophyQuote")}
          </Text>
        </blockquote>
      </section>

      <section
        aria-labelledby="about-closing-title"
        className="border-t border-[var(--border)] pt-10"
      >
        <Heading as="h2" id="about-closing-title" level="section">
          {t("closingTitle")}
        </Heading>
        <Text tone="muted" size="sm" className="mt-3">
          {t("courseCount", { count: courses.length })}
        </Text>
        <div className="mt-6">{browseCoursesLink}</div>
      </section>
    </PageShell>
  );
}
